// src/features/auth/components/TwoFactorVerification.tsx
import React, { useState } from 'react';
import { ShieldCheckIcon, KeyIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import authService from '../services/authService';
import { TwoFactorVerifyRequest } from '../types';

interface TwoFactorVerificationProps {
  tempToken: string;
  email: string;
  onVerificationSuccess: (data: any) => void;
  onCancel: () => void;
}

const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({ 
  tempToken, 
  email,
  onVerificationSuccess, 
  onCancel 
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!verificationCode.trim()) {
      setError('Please enter the verification code.');
      setLoading(false);
      return;
    }

    try {
      // Use exact backend specification format
      const verifyData = {
        email: email,
        temp_token: tempToken,
        totp_code: verificationCode,
        is_backup_code: useBackupCode
      };
      
      const response = await authService.verify2FA(verifyData as any);
      
      // 2FA verification successful, proceed to login
      onVerificationSuccess(response);
      setLoading(false);
    } catch (err: any) {
      setError(err.error || err.message || 'Invalid verification code. Please try again.');
      setLoading(false);
    }
  };


  return (
    <div className="p-6 sm:p-8 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-lg shadow-xl w-full max-w-md">
      {/* Verification form */}
        <>
          <div className="text-center mb-6">
            <ShieldCheckIcon className="mx-auto h-12 w-12 text-primary-light dark:text-primary-dark" />
            <h2 className="mt-2 text-2xl font-semibold text-theme-text dark:text-dark-theme-text">
              Two-Factor Authentication
            </h2>
            <p className="mt-1 text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
              {useBackupCode 
                ? 'Enter one of your backup codes'
                : 'Enter the 6-digit code from your authenticator app'
              }
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label 
                htmlFor="verification-code"
                className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1"
              >
                {useBackupCode ? 'Backup Code' : 'Verification Code'}
              </label>
              <div className="relative">
                <input
                  id="verification-code"
                  name="verification-code"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, useBackupCode ? 8 : 6))}
                  className="appearance-none block w-full px-3 py-2 pl-10 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text text-center font-mono text-lg"
                  placeholder={useBackupCode ? '12345678' : '123456'}
                  maxLength={useBackupCode ? 8 : 6}
                />
                <KeyIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-tertiary dark:text-dark-theme-text-tertiary" />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  <p className="ml-2 text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || !verificationCode.trim()}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-button hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode(!useBackupCode);
                  setVerificationCode('');
                  setError(null);
                }}
                className="w-full text-sm text-primary-light dark:text-primary-dark hover:underline focus:outline-none"
              >
                {useBackupCode 
                  ? 'Use authenticator app instead' 
                  : 'Use backup code instead'
                }
              </button>

              <button
                type="button"
                onClick={onCancel}
                className="w-full py-2 px-4 border border-theme-border dark:border-dark-theme-border rounded-md text-sm font-medium text-theme-text dark:text-dark-theme-text hover:bg-theme-bg dark:hover:bg-dark-theme-bg"
              >
                Cancel
              </button>
            </div>
          </form>
        </>
    </div>
  );
};

export default TwoFactorVerification;
